import type { ContextType, ReactElement } from "react";
import { PureComponent } from "react";

import styled, { css } from "styled-components";

import type { IconName } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import type { IDropDownItem } from "@similarweb/ui-components";
import {
    EllipsisDropdownItem,
    NoBorderButton,
    TextWrapper,
    PopupClickContainer,
    ScrollArea,
} from "@similarweb/ui-components";

import type { ECategoryType } from "app/common/services/categoryService.types";
import { TrackContext } from "pro-features/components/WithTrack/src/context";

import TrackProvider from "../../WithTrack/src/TrackProvider";
import I18n from "../../WithTranslation/src/I18n";
import type { ITranslateProp } from "../../WithTranslation/src/TranslationProvider";
import TranslationProvider from "../../WithTranslation/src/TranslationProvider";

import { TableSelectionKeywordsWarning } from "./TableSelectionKeywordsWarning";
import { TableSelectionNewGroup } from "./TableSelectionNewGroup";

const DropDownButton = styled(NoBorderButton)`
    ${TextWrapper} {
        color: ${colorsPalettes.carbon[0]};
    }
    .SWReactIcons path {
        fill: ${colorsPalettes.carbon[0]};
    }
`;

const DropDownItem = styled(EllipsisDropdownItem)<IDropDownItem>`
    ${css`
        svg {
            width: 100%;
            height: 100%;
        }
    `}
`;

const ItemStyled = styled(EllipsisDropdownItem)<any>`
    padding: 0 18px;
    margin-bottom: 1px;
    position: relative;
    ${({ appendSeparator }) =>
        appendSeparator &&
        css`
            &:after {
                content: "";
                width: calc(100% - 16px);
                height: 1px;
                background-color: ${colorsPalettes.carbon[100]};
                position: absolute;
                bottom: 0;
                left: 50%;
                transform: translateX(-50%);
            }
        `}
    ${({ disableHover }) =>
        disableHover &&
        css`
            &:hover {
                background-color: inherit;
                cursor: default;
            }
        `}
    svg {
        width: 100%;
        height: 100%;
    }
`;

export enum ETableSelectionNewGroupDropdownMode {
    NEW_GROUP,
    ALL_ITEMS_EXISTS,
    MAX_ITEMS,
    GROUP_LIST,
}

interface ITableSelectionNewGroupDropdownProps extends ITranslateProp {
    mode?: ETableSelectionNewGroupDropdownMode;
    groups: Array<{ id: string; text: string }>;
    onSubmit: (name: string) => void;
    isLoading: boolean;
    error?: boolean;
    errorMessage?: string;
    currentGroup?: string;
    onGroupClick?: (item?: FixmeType) => void;
    onCancel?: () => void;
    appendTo?: string;
    groupIconName?: IconName;
    newGroupNameLabel: string;
    trackingCategory?: string;
    allItemsExistsMessage?: string;
    maxItemsMessage?: string;
    newGroupItemText: string;
    onListTypeSelect?: (typeId: FixmeType) => void;
    groupType?: "website" | "keyword" | "segment";
    selectedListType?: ECategoryType;
    renderDropdownButton?: () => ReactElement;
    onToggleDropdown?: (isOpen: boolean) => void;
}

interface ITableSelectionNewGroupDropdownState {
    isOpen: boolean;
    groupName: string;
}

// height of popup - first item size - top & bottom padding
const scrollAreaHeight = 256 - 48 - 8 * 2;

export class TableSelectionNewGroupDropdown extends PureComponent<
    ITableSelectionNewGroupDropdownProps,
    ITableSelectionNewGroupDropdownState
> {
    static contextType = TrackContext;

    context: ContextType<typeof TrackContext>;

    public static defaultProps = {
        mode: ETableSelectionNewGroupDropdownMode.GROUP_LIST,
        count: 0,
        appendTo: ".swReactTable-header-wrapper",
        trackingCategory: "",
        allItemsExistsMessage: "",
        maxItemsMessage: "",
        newGroupItemText: "",
    };

    public state = {
        isOpen: false,
        groupName: "",
    };

    // @ts-ignore FIXME: strict migration - popupRef implicitly has 'any' type — complex popup ref type from ui-components
    private popupRef;

    public render() {
        const config = {
            width: 320,
            maxHeight: 330,
            enabled: true,
            placement: "ontop-left",
            cssClassContent: "Popup-content--pro-dropdown",
            onToggle: this.onToggle.bind(this),
        };
        return (
            <PopupClickContainer
                content={this.getContent}
                config={config}
                ref={this.setRef}
                appendTo={this.props.appendTo}
            >
                <div>{this.getDropdownButtonComponent()}</div>
            </PopupClickContainer>
        );
    }

    private getDropdownButtonComponent = () => {
        const { renderDropdownButton } = this.props;

        // In case the user has provided a custom button - render the dropdown
        // button using that function, otherwise - render the default button
        return renderDropdownButton ? (
            renderDropdownButton()
        ) : (
            <DropDownButton>
                <I18n>table.selection.keywords.selectgroup</I18n>
            </DropDownButton>
        );
    };

    private getContent = () => {
        const firstItem = this.getFirstItem();
        const content = this.getItems();
        // @ts-ignore FIXME: strict migration - track possibly undefined — TrackProvider expects non-optional but context can be undefined
        const track = this.context?.track ?? (() => {});

        // must use TranslationProvider since the dropdown content is rendered in a different tree
        return (
            <div>
                <TrackProvider track={track}>
                    <TranslationProvider translate={this.props.translate}>
                        {firstItem}
                        {this.props.mode !== ETableSelectionNewGroupDropdownMode.GROUP_LIST ? (
                            content
                        ) : (
                            <ScrollArea
                                style={{ maxHeight: scrollAreaHeight, minHeight: 0 }}
                                verticalScrollbarStyle={{ borderRadius: 5 }}
                                horizontal={false}
                                smoothScrolling={true}
                                minScrollSize={48}
                            >
                                {content}
                            </ScrollArea>
                        )}
                    </TranslationProvider>
                </TrackProvider>
            </div>
        );
    };

    private getFirstItem = () => {
        switch (this.props.mode) {
            case ETableSelectionNewGroupDropdownMode.NEW_GROUP:
                return (
                    <ItemStyled
                        disableHover={true}
                        id="add-new"
                        key={`item-new`}
                        iconName="add"
                        appendSeparator={true}
                        onClick={this.props.onGroupClick}
                    >
                        {this.props.newGroupItemText}
                    </ItemStyled>
                );
            case ETableSelectionNewGroupDropdownMode.ALL_ITEMS_EXISTS:
                return (
                    <ItemStyled
                        disableHover={true}
                        appendSeparator={true}
                        key={`item-group`}
                        iconName={this.props.groupIconName}
                        iconSize="sm"
                    >
                        {this.props.currentGroup}
                    </ItemStyled>
                );
            case ETableSelectionNewGroupDropdownMode.MAX_ITEMS:
                return (
                    <ItemStyled
                        disableHover={true}
                        appendSeparator={true}
                        key={`item-group`}
                        iconName={this.props.groupIconName}
                        iconSize="sm"
                    >
                        {this.props.newGroupItemText}
                    </ItemStyled>
                );
            case ETableSelectionNewGroupDropdownMode.GROUP_LIST:
                return (
                    <ItemStyled
                        id="add-new"
                        key={`item-new`}
                        iconName="add"
                        appendSeparator={this.props.groups.length > 0}
                        onClick={this.props.onGroupClick}
                    >
                        {this.props.newGroupItemText}
                    </ItemStyled>
                );
        }
    };

    private getItems = () => {
        const { translate } = this.props;
        switch (this.props.mode) {
            case ETableSelectionNewGroupDropdownMode.NEW_GROUP:
                return [
                    <TableSelectionNewGroup
                        key="new-group"
                        title={this.props.newGroupNameLabel}
                        placeholder={translate("table.selection.newgroup.keywords.placeholder")}
                        onCancel={this.onCancel}
                        onSubmit={this.onSubmit}
                        submitButtonDisabled={false}
                        error={this.props.error}
                        errorMessage={this.props.errorMessage}
                        onListTypeSelect={this.props.onListTypeSelect}
                        groupName={this.state.groupName}
                        onGroupNameChange={this.onGroupNameChange}
                        isLoading={this.props.isLoading}
                        groupType={this.props.groupType}
                        selectedListType={this.props.selectedListType}
                    />,
                ];
            case ETableSelectionNewGroupDropdownMode.ALL_ITEMS_EXISTS:
                return [
                    <TableSelectionKeywordsWarning
                        key="all-items-exist"
                        onCancel={this.onCancel}
                        message={this.props.allItemsExistsMessage ?? ""}
                    />,
                ];
            case ETableSelectionNewGroupDropdownMode.MAX_ITEMS:
                return [
                    <TableSelectionKeywordsWarning
                        key="max-items"
                        onCancel={this.onCancel}
                        message={this.props.maxItemsMessage ?? ""}
                    />,
                ];
            case ETableSelectionNewGroupDropdownMode.GROUP_LIST:
                return this.props.groups.map((item, index) => {
                    return (
                        <DropDownItem
                            key={`item-${index}`}
                            id={item.id}
                            iconName={this.props.groupIconName}
                            iconSize="sm"
                            onClick={this.onItemClick(item)}
                        >
                            {item.text}
                        </DropDownItem>
                    );
                });
        }
    };

    private onSubmit = () => {
        this.props.onSubmit(this.state.groupName);
    };

    private onItemClick = (item: FixmeType) => () => {
        this.props.onGroupClick?.(item);
    };

    private onToggle = (isOpen: boolean) => {
        const track = this.context?.track;
        const { trackingCategory, onToggleDropdown } = this.props;
        track?.(trackingCategory ?? "", `${isOpen ? "open" : "close"}`, "dropdown");

        if (onToggleDropdown) {
            onToggleDropdown(isOpen);
        }
    };

    private onGroupNameChange = (value: string) => {
        this.setState({
            groupName: value,
        });
    };

    private setRef = (ref: FixmeType) => {
        this.popupRef = ref;
    };

    public close = () => {
        return this.popupRef.closePopup();
    };

    private onCancel = () => {
        this.setState(
            {
                groupName: "",
            },
            () => {
                this.props.onCancel?.();
            },
        );
    };
}
